import { Button, IconButton } from "@dropbox/dig-components/buttons";
import { Text, Title } from "@dropbox/dig-components/typography";
import { Box, Cluster, Stack } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  CalendarLine,
  ChevronDownLine,
  ChevronUpLine,
  EmailLine,
  SlackExternalLogo,
} from "@dropbox/dig-icons/assets";
import { RichTextArea } from "@mirage/mosaics/BaseProfilePage/RichTextArea/RichTextArea";
import { ProfileEmployees } from "@mirage/mosaics/UserProfilePage/ProfileEmployees";
import { UserProfileConfetti } from "@mirage/mosaics/UserProfilePage/UserProfileConfetti";
import { UserProfileDetails } from "@mirage/mosaics/UserProfilePage/UserProfileDetails";
import {
  canUserEditUserProfile,
  getEditUserProfilePageHref,
} from "@mirage/mosaics/UserProfilePage/utils";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import i18n, { Trans } from "@mirage/translations";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import type { UserInfo } from "@mirage/mosaics/UserProfilePage/types";

interface UserProfileBodyProps {
  user: UserInfo;
}

const getConfettiType = (user: UserInfo) => {
  const currentDate = new Date();
  const currentMonth = String(currentDate.getMonth() + 1).padStart(2, "0");
  const currentDay = String(currentDate.getDate()).padStart(2, "0");
  const formattedCurrentDate = `${currentMonth}/${currentDay}`;

  if (user.is_deactivated) {
    return undefined;
  }

  if (user.birthday === formattedCurrentDate) {
    return "birthday";
  }

  if (user.tenure !== null && user.tenure! > 0 && user.tenure! % 12 == 0) {
    return "anniversary";
  }

  return undefined;
};

export const UserProfileBody = ({ user }: UserProfileBodyProps) => {
  const currentAccount = useDropboxAccount();
  const canEdit = canUserEditUserProfile(currentAccount?.email, user.email);
  const confettiType = getConfettiType(user);

  return (
    <>
      {confettiType && (
        <UserProfileConfetti userId={user.user_id} type={confettiType} />
      )}
      <UserProfileButtonGroup />
      <Stack gap="32">
        <Box as="div">
          {!user.is_deactivated && <UserProfileDetails user={user} />}
        </Box>
        {user.reports && user.reports.length > 0 && (
          <ProfileEmployees
            sectionTitle={i18n.t("user_profile_direct_report_title")}
            employees={user.reports}
            employee={user}
          />
        )}
        {user.peers && user.peers.length > 0 && (
          <ProfileEmployees
            sectionTitle={i18n.t("user_profile_peer_title")}
            employees={user.peers}
            employee={user}
          />
        )}
        {(canEdit || user.about_me) && <AboutMeSection user={user} />}
      </Stack>
    </>
  );
};

const AboutMeSection = ({ user }: { user: UserInfo }) => {
  const aboutMe = user.about_me;
  const [isAboutMeHidden, setIsAboutMeHidden] = useState(false);

  return (
    <Stack>
      <Box as="div" display="flex" alignItems="center" style={{ gap: "4px" }}>
        <Title size="small" weightVariant="emphasized" tagName="span">
          {i18n.t("user_profile_about_me_string")}
        </Title>
        <IconButton
          variant="transparent"
          size="small"
          onClick={() => setIsAboutMeHidden(!isAboutMeHidden)}
        >
          {isAboutMeHidden ? (
            <UIIcon src={ChevronDownLine} />
          ) : (
            <UIIcon src={ChevronUpLine} />
          )}
        </IconButton>
      </Box>
      {!isAboutMeHidden && (
        <Box paddingBottom="12">
          {aboutMe ? (
            <RichTextArea theme="small-faint" value={aboutMe} />
          ) : (
            <AboutMePlaceholder user={user} />
          )}
        </Box>
      )}
    </Stack>
  );
};

const AboutMePlaceholder = ({ user }: { user: UserInfo }) => {
  const navigate = useNavigate();
  return (
    <Box
      as="div"
      display="flex"
      alignItems="center"
      marginTop="12"
      style={{ gap: "4px" }}
    >
      <Text color="faint">
        <Trans
          i18nKey="user_profile_about_me_placeholder"
          components={{
            tag: (
              <Text
                style={{ textDecoration: "underline", cursor: "pointer" }}
                color="faint"
                onClick={() => {
                  const editUrl = getEditUserProfilePageHref(user.email);
                  if (editUrl) {
                    navigate(editUrl);
                  }
                }}
              />
            ),
          }}
        />
      </Text>
    </Box>
  );
};

const UserProfileButtonGroup = () => {
  return (
    <Box marginBottom="24">
      <Cluster gap="8">
        <Cluster.Item>
          <Button
            variant="primary"
            withIconStart={<UIIcon src={SlackExternalLogo} />}
          >
            {i18n.t("user_profile_button_message_string")}
          </Button>
        </Cluster.Item>
        <Cluster.Item>
          <IconButton
            variant="opacity"
            style={{ borderRadius: "var(--spacing__unit--1)" }}
          >
            <UIIcon src={EmailLine} />
          </IconButton>
        </Cluster.Item>
        <Cluster.Item>
          <IconButton
            variant="opacity"
            style={{ borderRadius: "var(--spacing__unit--1)" }}
          >
            <UIIcon src={CalendarLine} />
          </IconButton>
        </Cluster.Item>
      </Cluster>
    </Box>
  );
};
